
import React from 'react'
import { useLocation } from 'react-router-dom'

import {
    Box,
    Divider,
    Stack,
    Typography,
    Avatar,
    Skeleton,
    useTheme
} from '@mui/material'

import {
    Breadcrumbs
} from '@repo/ui'
import {
    StatusChip
} from 'components'
import { CompanyAvatar } from 'components/display/CompanyAvatar/CompanyAvatar'
// Direct subpath, not the `components/onboarding` barrel — for the same reason
// CompanyAvatar above is: that barrel re-exports ToursDialog, which imports
// `components/input/Search`, and the resulting edge would be a cycle back into a
// barrel this file is itself reachable from.
import { TakeATourChip } from 'components/onboarding/TakeATourChip'
import { AskVeraLauncher } from './AskVeraLauncher'
// Direct file import (not the barrel) to avoid a navigation/-barrel import cycle.
// `Building2` was referenced below with no import in this file at all — a
// ReferenceError for any header rendering a company avatar that has no logo.
// Invisible until apps/platform's typecheck was repaired (it was one of the 40
// pre-existing TS2304s the TypeScript 5 upgrade surfaced).
import { Building2 } from 'lucide-react'
import { ReferrerBackLink, useHasReferrerBack } from '../ReferrerBackLink/ReferrerBackLink'
// Direct file, not the `routes` barrel: the constants are all this needs (VRG-738).
import { PAGE_LABELS } from 'routes/routes'

export interface HeaderProps {
    avatar?: {
        url: string
        letters: string
        isCompany?: boolean
        /** Company name — used to render initials for the company placeholder. */
        name?: string
    }
    /**
     * Widened from `string` to a node so a loading state can pass a `<Skeleton>` bar in the
     * title's place. Rendered straight into a `Typography` with no string operations, so this is
     * safe — and a skeleton bar is the only way to avoid text-of-one-length being swapped for
     * text of another (§19).
     */
    title?: React.ReactNode
    /**
     * Small line under the title — an identifier, not a description.
     *
     * Exists for records whose TITLE is human text and whose reference is a number: a support
     * request's header read "Request #1011", which names nothing, and the two status chips beside
     * a bare number had nothing to anchor to. Title carries the words, subtitle carries the id.
     *
     * Deliberately NOT for prose. Page-level descriptive copy belongs under the title as a page
     * description (`description` below), which is a different, wider treatment.
     */
    subtitle?: React.ReactNode
    /**
     * A small node rendered immediately AFTER the title, on the title's own row.
     *
     * For an annotation about the RECORD that is not its lifecycle state — VRG-361's
     * "this row is not real customer data" badge is the first and only user. Distinct
     * from `status`, which is the lifecycle pill and takes exactly one value, and from
     * `leftComponents`, which is the sub-tabs slot and gets moved to its own row when
     * `stackControls` is set.
     *
     * The row it lands in already exists for `TakeATourChip`, so this costs nothing on
     * the pages that do not pass it and cannot move the 68.5px title centre other pages
     * are measured against.
     */
    titleAdornment?: React.ReactNode
    /**
     * One or two sentences describing the PAGE as a whole — scope, caveats, "what this can't do".
     *
     * Sits directly under the title as part of the header block, never at the foot of the page
     * (working-conventions §40): a caveat read after the user has already acted cannot inform the
     * decision it qualifies.
     *
     * Owned here rather than hand-rolled per page, mirroring `apps/app`'s `components/UI/PageHeader`.
     * Before this existed, a page that wanted one rendered its own `<Typography>` AFTER `<Header>`,
     * so the gap it got was this component's `pb: 6` PLUS whatever the page happened to set — which
     * is exactly the drift §40 forbids, and measured 40px on the feature board against the 16px the
     * rule asks for.
     *
     * Contrast with `subtitle`: that is an IDENTIFIER (a record's number) inside the title's own
     * column, next to the title; this is PROSE at the header's full width, below the title row.
     */
    description?: React.ReactNode
    total?: number | boolean
    /**
     * Replaces the LAST breadcrumb segment (which is otherwise a raw id from the URL).
     *
     * Widened from `string | boolean` to a node so a loading state can pass a `<Skeleton>` for the
     * crumb it does not know yet. This is load-bearing for skeleton parity, not cosmetic: `Header`
     * renders `<Breadcrumbs>` ONLY when this is truthy, so a skeleton that omitted it got an EMPTY
     * 8px wayfinding row against the loaded page's ~29px one — measured, that alone put the
     * skeleton's content 25px higher than the content it stands in for.
     */
    breadcrumbsOveride?: React.ReactNode
    breadcrumbs?: boolean
    /**
     * Suppress the contextual "← Back to <referrer>" link, AND the wayfinding row it would
     * otherwise occupy.
     *
     * It does NOT mean "some other header will show it instead". That was the original
     * justification — "Settings' top header defers to the per-page header" — and it was
     * unachievable: an inner header is passed `nested`, and `nested` skips the wayfinding row
     * entirely, so there was no header anywhere in the tree that could render the link. Every
     * /settings section silently dropped it, including the ones already being handed a referrer.
     *
     * What it is legitimately for: a page whose own layout has to own the header's box, so the
     * empty-row lift below cannot reach it. SettingsPage is the one such caller — its sticky sx
     * replaces `position: relative` with `sticky` — and it passes `hideReferrerBack={!hasBack}`,
     * i.e. skip the row only when nothing would occupy it.
     */
    hideReferrerBack?: boolean
    info?: boolean
    status?: {
        statuses: object
        value: string
    }
    certificateProduct?: {
        name: string
        linkToIndex?: boolean
    }
    // primaryButton?: boolean
    // switch?: boolean
    leftComponents?: React.ReactNode
    rightComponents?: React.ReactNode,
    /**
     * Drop leftComponents (sub-tabs) + rightComponents (primary action) onto a
     * dedicated toolbar row BELOW the title instead of inline with it. Use on pages
     * that have section tabs (e.g. Billing, Roles & Permissions) so the title reads
     * as the page header and the tabs/actions read as its controls. Title-only pages
     * keep the default inline layout.
     */
    /**
     * This Header is a SECTION title inside a panel, not the page title.
     *
     * Page headers reserve top space so every page's title lands at the same 53px, and render a
     * wayfinding row for breadcrumbs / the back-link. A nested header needs neither: the page header
     * above it already did both, so that reservation becomes pure empty space. Measured on
     * /settings/appearance before this existed: 86px between the "Settings" title and the
     * "Appearance" title, most of it reserved for wayfinding that never renders here.
     */
    nested?: boolean
    /**
     * Height of the header's action buttons: `medium` (40, the default) or `large` (49).
     *
     * `large` is the escape hatch for a header that shares its band with a 49px field and needs
     * field-height parity. No page currently does. See the note on `headerActionGroupSx`.
     */
    actionSize?: 'medium' | 'large'
    stackControls?: boolean
    /**
     * Suppress the global Ask Vera launcher this Header otherwise renders on every
     * non-nested page.
     *
     * The default is ON, deliberately not opt-in per page — see `AskVeraLauncher`'s
     * own docblock for why an opt-in prop here would repeat working-conventions §42's
     * "35 of 37 call sites forgot it." This escape hatch exists for the rare page that
     * genuinely cannot host it (none does today), not as the normal path.
     */
    hideAskLauncher?: boolean
    sx?: object
}

/**
 * Height and gap for a page header's action group, enforced HERE rather than left to each page.
 *
 * Both numbers were previously whatever the page passed, and the pages did not agree: a survey of
 * the 17 that set a gap found 8 at 12px, 7 at 16px and 2 at 24px. So working-conventions §29's
 * claim that header groups are 16px was never actually the rule — it described a plurality that
 * did not exist. Enforcing it in one place is what makes it true.
 *
 * HEIGHT 40px (`size="medium"`), reversing an earlier decision to use 49 (`large`). The reasoning
 * for 49 was that at 40 the header's primary actions were 9px shorter than the table toolbar's
 * secondary controls beneath them — "the most important buttons reading as the smallest".
 *
 * That argument does not survive inspection. The TOOLBAR is 49 because it shares a row with a 49px
 * search FIELD: that is field-height parity, not a statement of importance. The header then matched
 * the toolbar, inheriting a constraint that does not apply to it — a page header has no field in it
 * (verified: of 31 headers with action groups, the only one containing a field was the docs card
 * index, whose header held a Search and no buttons at all, so this height never applied there. That
 * page was deleted in VRG-402 — the docs index is now the reader layout, whose search lives in the
 * left nav column — so NO page header contains a field and the exception is gone entirely.)
 *
 * What actually carries primary emphasis is FILL, not height: `variant="primary"` is a solid dark
 * button among outlined secondaries, and that reads identically at 40. Meanwhile the cost of 49 is
 * structural — the title's line box is 31px, so a 49px button centred on the same line spans
 * 19.5→68.5 and the BUTTONS, not the title, set the header's height. At 49 every page opens with a
 * heavy band; it also partly spends back the 24.5px the title lift was for.
 *
 * The residual objection, and it is real: on Users/Roles/Billing a nested header sits directly above
 * a 49px toolbar, so the page's primary action is now shorter than a tertiary Layout toggle
 * (measured 40 against 49). Accepted, because the two are visually separate bands — a right-aligned
 * action group in a title row versus a bordered control strip beside a search box — and do not read
 * as one row being compared. `actionSize="large"` remains for any page that needs the old height.
 *
 * GAP 12px, down from 16. §29's reasoning for 16 was that headers sit in more open space; in
 * practice the extra 4px read as the buttons drifting apart from each other, and matching the
 * toolbar's 12px means the top area has one gap as well as one height.
 *
 * The selector targets the direct children of the action group's own Stack, which is where a
 * page's `spacing={n}` puts its margins — so this overrides whatever the page passed without
 * reaching into nested stacks (a button's own icon+label spacing is untouched).
 */
const headerActionGroupSx = (actionSize: 'medium' | 'large') => ({
  '& > .MuiStack-root > :not(style) ~ :not(style)': { marginLeft: '12px' },
  '& > :not(style) ~ :not(style)': { marginLeft: '12px' },
  /**
   * 49 (the Button's `large`) by default; 40 (`medium`) when the page asks.
   *
   * 49 exists to match the shared Input's rendered height, because a page header's action row
   * usually sits in the same visual band as search and filter controls and has to line up with
   * them. The enforcement has to be here rather than left to each call site — otherwise a page
   * passing `size="medium"` is silently overridden back to 49, which is what happened to the
   * support page's "New request".
   *
   * OPT-IN per page rather than derived from `nested`, which was the first attempt and was
   * wrong: on Users / Roles / Billing the nested header sits directly above a TABLE TOOLBAR whose
   * Download/Filter/Sort/Layout buttons are 49 to match the search field they share a row with.
   * Dropping only the header to 40 there made the page's primary action SMALLER than a tertiary
   * layout toggle — measured 40 against 49 — inverting the hierarchy it was meant to fix. Pages
   * with no toolbar under the header (the support lists) have no such constraint, so they ask.
   */
  '& .MuiButton-root': { height: actionSize === 'large' ? 49 : 40 },
} as const)

/**
 * The two tuned numbers for a page `description` (§40). MEASURED in the browser, not derived.
 *
 * §40's warning is not a formality and was reproduced here: line-height leading means the distance
 * the eye reads is not the margin you set, and the two neighbours carry different leading, so the
 * same change moves the two gaps by different amounts. Both numbers below were obtained by
 * `getBoundingClientRect` on /support/requests at 1280×800 and iterating until §40's trio held.
 *
 * MEASURED, before → after (rect-to-rect: the h5's line box, the description's inline box, and the
 * first content element's border box):
 *
 *   title → description ......... 23.9px → 15.9px   (§40 target 16)
 *   description → first content .. 30.4px → 32.4px   (§40 target 32)
 *   title top ................... 28.4px → 28.4px   (unchanged — the description is BELOW it)
 *
 * The "before" is the hand-rolled arrangement this replaces: a `<Typography sx={{pb:4}}>` rendered
 * after `<Header>`, so its top gap was the header's own `pb: 6` (24px) and its bottom gap was the
 * page's 16px. That is the drift §40 names — the copy ended up nearer the content it qualifies than
 * the title it belongs to.
 *
 * WHY marginTop IS NEGATIVE. The description is a sibling ROW after the title row, so its natural
 * gap is the title row's own slack PLUS the Stack's 12px: the title's 31.2px line box is centred in
 * a 49px row (`minHeight: 49`, so the title does not move when a page has no action buttons),
 * leaving ~8.9px below it. 8.9 + 12 = 20.9 measured, against a 16 target. This is the row's own
 * centring being paid back, not an arbitrary offset.
 *
 * WHY marginBottom IS NEGATIVE. The header's `pb: 6` (24px) is the title→content gutter every page
 * has, and it must stay exactly that for pages with no description — so the adjustment lives on the
 * description block, where a page without one has no element to match. CAVEAT, and it is a real
 * one: the "first content" this was tuned against is a `<Grid container spacing={3}>`, whose own
 * −12px top margin and 12px item padding sit inside the measured gap. A page whose content root has
 * no such padding will read ~6px tighter than 32. Re-measure rather than assume if you add a
 * description to a page with a different content shape — that is exactly what §40 asks for.
 *
 * The selector is `& > .vera-page-description` — a class, specificity (0,2,0) — because Stack's own
 * `& > :not(style) ~ :not(style)` spacing rule is (0,1,2) and beats anything set on the child's own
 * `sx`. Setting `mt` on the child was the first attempt and silently did nothing.
 */
const PAGE_DESCRIPTION_MT = '4px'
const PAGE_DESCRIPTION_MB = '-6px'

/**
 * `forwardRef` so a page that PINS this header can MEASURE the band it occupies.
 *
 * Not cosmetic. Every sticky element below a pinned header has to be offset by that
 * header's height, and both constants that tried to hold that number as a literal have
 * gone stale when this component's geometry moved — the WARNING in the `sx` block below
 * names `SETTINGS_HEADER_H` and `DOCS_SLAB_TOP` as needing re-derivation, and `LABS_HEADER_H`
 * was not on that list and duly went 29px wrong, clipping the Labs sub-nav's first row. A
 * caller holding a ref measures instead, which cannot drift. `LabsPage` is the first.
 */
export const Header = React.forwardRef<HTMLDivElement, HeaderProps>((props, ref) => {
    const {
        avatar = false,
        title,
        subtitle,
        titleAdornment,
        description,
        total = false,
        status = false,
        rightComponents,
        leftComponents,
        breadcrumbs,
        breadcrumbsOveride = false,
        hideReferrerBack = false,
        certificateProduct,
        nested = false,
        actionSize = 'medium',
        stackControls = false,
        hideAskLauncher = false,
        sx = {}
    } = props
    const theme = useTheme()
    const location = useLocation()

    /**
     * Does this header show a VISIBLE breadcrumb trail?
     *
     * Not the same question as "does the wayfinding row render". The row renders whenever there
     * could be a back-link, and on a top-level page it renders EMPTY — 8px of padding around a
     * `Breadcrumbs` that draws nothing for a single path segment and a `ReferrerBackLink` that
     * returns null. /products and /certificates are that case; /support/:id is not.
     *
     * The distinction matters because the two want opposite treatment: an empty row is dead space
     * and its page's title should rise to the brand mark's line, while a real trail needs that space
     * and should itself sit on the mark's line with the title below it.
     */
    const wayfindingRowRenders = Boolean(breadcrumbsOveride) || !hideReferrerBack
    /**
     * A trail is visible IFF `breadcrumbsOveride` is truthy — because that is literally what gates
     * `<Breadcrumbs>` below. It is not "more than one path segment", which is what this said first
     * and which is wrong in a way that produces the worst of both outcomes: a page with a deep URL
     * and no override got an EMPTY wayfinding row (nothing to render) AND no title lift (the
     * predicate thought a trail was there). GlossaryPage at /docs/reference/glossary hit exactly
     * that — three segments, no override, so a blank 33px band and a title 24.5px low.
     *
     * Note `breadcrumbs?: boolean` does NOT gate anything here; only the override does.
     */
    /*
      Does the wayfinding row have CONTENT — a breadcrumb trail OR a back link?

      It used to ask only about the trail, which meant a page arrived at via a
      reference link got the lift meant for pages whose row is EMPTY: the whole
      header shifted up 24.5px, putting the back link 8px from the window edge and
      its title 20px above every other page's. That is the same cramping the comment
      below already describes for trails ("pushed the trail to 9.5px from the window
      edge, which read cramped") — the exemption was simply never extended to the
      other thing that can occupy the row.

      `useHasReferrerBack` is the back link's OWN predicate, exported so the row's
      height and its contents are decided by one rule. Asking `!hideReferrerBack`
      instead would be wrong in the common case: almost no page sets that flag, yet
      almost no page actually shows a link — it renders only when router state
      carries a referrer and the destination is not an ancestor of it.
    */
    const hasReferrerBack = useHasReferrerBack()
    const wayfindingRowHasContent = Boolean(breadcrumbsOveride) || (!hideReferrerBack && hasReferrerBack)
    const showsBreadcrumbTrail = wayfindingRowHasContent

    return (
        <Stack
        ref={ref}
        // Breadcrumb → title gap. This Stack's only two children are the
        // breadcrumb row and the title row, so this IS that gap — the single
        // source for every platform page. 12px keeps them reading as one header
        // unit rather than two stacked blocks. See working-conventions §37.
        spacing={3}
        sx={{
            pb: 6,
            // Reserve the wayfinding row's vertical space even when that row does not render, so a
            // page title always sits the same distance from the top of the page.
            //
            // Measured across pages before this: /products, /people and /tags put their title at
            // 53px while /settings put it at 28px — a 25px inconsistency with two separate causes.
            // 20px of it is this row (its own `pt: 2` = 8px, plus the 12px Stack spacing below it),
            // which renders only when there is a breadcrumb or a back-link. The other ~5px is the
            // title row's height, handled below.
            //
            // Reserving space rather than rendering an empty row keeps the DOM identical in both
            // cases; only the offset is constant.
            // Nested headers reserve nothing: the page header above already set the page's top
            // rhythm, so reserving it again is just a gap.
            // Aligns the page title with the sidebar's brand mark (VRG-221).
            //
            // This used to RESERVE the wayfinding row's 20px so a page with no breadcrumb put its
            // title at the same 52.9px as a page with one. Measured, that put every title's optical
            // centre at 68.5 while the mark's sits at 44 — the two topmost elements on screen, one
            // per column, not sharing a line.
            //
            // -4.5px rather than 0: dropping the 20px reservation gets the title to centre 48.5,
            // and the remaining 4.5 comes from the title row's 8.9px lead-in inside its 49px box.
            // Negative because Layout's own `p: 6` already puts the header 24px down and the target
            // (19.5px) is above that. 24 − 4.5 + 8.9 = 28.4 top, centre 44 — dead on the mark.
            //
            // NOTE the geometry this cannot fix: a page with a VISIBLE breadcrumb needs that row
            // above the title, so its title necessarily sits ~20px lower. Top-level pages align
            // with the mark; drill-in pages carry a trail above the title instead. There is no
            // arrangement where both are true, because the row has to go somewhere.
            //
            // WARNING: `SETTINGS_HEADER_H` is calibrated against this header's height and does NOT
            // track it. It was re-derived (93 → 68.5) with this change. `DOCS_SLAB_TOP` is
            // calibrated against the title's POSITION and was re-derived too. Re-measure both if
            // you touch this.
            // `position: relative` + `top`, NOT a negative margin. The margin was tried and does
            // nothing here: this Stack is the first in-flow child of a block parent, so a negative
            // margin-top COLLAPSES through the parent instead of shifting the child — measured, the
            // root kept `marginTop: -4.5px` and a top of 24 regardless. Relative offset moves the
            // painted box without touching flow, which is also what we want: everything BELOW the
            // header keeps its position, so no downstream sticky offset has to be re-derived a
            // second time.
            // A page with a REAL trail keeps its original top margin: the trail needs the row, and
            // restoring it also lands the trail on the mark's own line (measured, the trail's top is
            // 32 against the mark's 31.5 — they were already top-aligned before any of this, which
            // is why nothing extra is needed to achieve it). Shifting those pages up had pushed the
            // trail to 9.5px from the window edge, which read cramped.
            ...(!nested && !showsBreadcrumbTrail && {
                position: 'relative',
                // Two different offsets, because there are two different layouts above the title
                // and both have to land its centre on 44 (the brand mark's).
                //
                //   no wayfinding row  title row starts at the header's own top (24), text 8.9px
                //                      in → centre 48.5. Needs -4.5.
                //   wayfinding row     an 8px row + the 12px Stack gap sit above the title row, so
                //                      it starts at 44 → centre 68.5. Needs -24.5.
                //
                // A single number cannot do both; that is why /products and /certificates did not
                // move when only the reservation branch was changed.
                top: wayfindingRowRenders ? '-24.5px' : '-4.5px',
                // Negative marginBottom of the SAME amount, so the space is RECLAIMED rather than
                // merely vacated. `top` alone moves the header's painted box but leaves its flow
                // box behind, which left a dead band: on /products the title rose 24.5px while the
                // toolbar and the table stayed put. Pulling the following content up by the same
                // amount closes the band, and the table's `fillBottom` re-measures its own top each
                // layout so it stretches into the reclaimed height instead of just sliding up.
                marginBottom: wayfindingRowRenders ? '-24.5px' : '-4.5px',
            }),
            // A section title also does not need the page header's 24px bottom gutter — the panel
            // it introduces provides its own spacing.
            ...(nested && { pb: 3 }),
            // Page-description gaps. Scoped to the description's own class, so a header without one
            // has no matching element and this rule is inert. See PAGE_DESCRIPTION_MT/MB.
            '& > .vera-page-description': {
                marginTop: PAGE_DESCRIPTION_MT,
                marginBottom: PAGE_DESCRIPTION_MB,
            },
            ...sx,
        }}
        >
            {/* Wayfinding row: breadcrumb trail + the cross-surface back-link.
                The back-link used to trail the TITLE, where it read as an
                afterthought hanging off the page name — easy to miss, and it made
                the title row's width depend on whether you arrived by a reference
                link. Both controls answer "how did I get here", so they share a
                row; §42 keeps them from being redundant (the back-link is
                suppressed when the referrer is just the breadcrumb parent).
                Rendered when EITHER is present, so a page with a back-link but no
                breadcrumb still has somewhere to put it. */}
            {!nested && (breadcrumbsOveride || !hideReferrerBack) && (
                <Stack pt={2} direction="row" alignItems="center" spacing={4}>
                    {breadcrumbsOveride && (
                        <Breadcrumbs
                            breadcrumbsOveride={breadcrumbsOveride as any}
                            pathLabels={PAGE_LABELS}
                            certificateProduct={certificateProduct}
                        />
                    )}
                    {!hideReferrerBack && <ReferrerBackLink />}
                </Stack>
            )}
            <Stack
            direction="row"
            spacing={3}
            pt={0}
            // The title row is 49px tall whether or not it holds action buttons. Without this the
            // row collapsed to the title's own height on pages with no actions, and since the title
            // is vertically centred, its top moved by (49 - 31) / 2 = 9px depending on whether the
            // page happened to have a button in the header. A page title's position should not be a
            // function of its action set.
            //
            // 49px is the action-button height (§29), so this is the row those buttons already
            // create on every page that has them.
            justifyContent="space-between"
            // alignItems="center"
            sx={{ width: '100%' }}
            >
                <Stack
                direction="row"
                spacing={3}
                // 49px, not 40. This is the box the title is vertically centred in, so its height
                // decides where the title sits — and at 40 the title landed 5px higher on pages
                // whose header has no action buttons (the buttons are 49px and stretched the row on
                // pages that have them). Measured: /settings 48px against /products 53px. 49 is the
                // action-button height (§29), i.e. the height those pages already had.
                //
                // THE TITLE YIELDS; THE STATUS INDICATORS NEVER DO.
                //
                // `minWidth: 0` is the load-bearing half and it is the part that gets missed. A
                // flex item's automatic minimum size is its MIN-CONTENT size, and a `noWrap`
                // title's min-content is its full text width — so without this, THIS Stack could
                // not shrink no matter how many `minWidth: 0`s the title's own ancestors carried
                // (they did, three deep, and it still overflowed). Measured on
                // /certificates/…006 at 1024w: this Stack sat at its 738px min-content, the
                // action group ran to x=1177 inside a 999px row, and the title rendered in full
                // with `scrollWidth === clientWidth` — i.e. nothing truncated and everything to
                // the right of the title was pushed off the screen instead.
                //
                // Which element gives way is a product decision, not a layout convenience. A
                // truncated TITLE is recoverable: you are already on that record's page, the full
                // name is in the breadcrumb, the Product card and the `title` attribute below. A
                // truncated STATUS INDICATOR — the Opened/seal pill, the invalid-scan count,
                // group/bundle info — silently withholds the thing that most changes what the
                // reader would do next, and a half-drawn pill is indistinguishable from no pill.
                // This is §49's argument for table headers ("size for the header, not the
                // values") one level up, applied to a page header.
                //
                // So: this column shrinks, the title inside it ellipsizes, and every other child
                // of this row — status chip, `leftComponents`, and the action group opposite —
                // carries `flexShrink: 0`. Do not shrink the title's font or the pill to make
                // something fit; the fix is which element yields.
                //
                // ⚠️ `minWidth: 0` ONLY — do NOT add `flex-grow`. This Stack carries
                // `justifyContent="space-between"`, which is inert while the column shrink-wraps
                // to its content but SPREADS its children the moment it grows. Adding
                // `flex: '1 1 auto'` here (tried, reverted) left the long-title case correct and
                // broke every short one: on ZZ-ACK-01 the column grew to 791px and the "Active"
                // chip detached from the title, sitting at x=783 with the empty `leftComponents`
                // slot flung to the far edge. Growth is not needed anyway — the outer row's own
                // `space-between` already holds the action group against the right edge.
                sx={{ minHeight: 49, minWidth: 0 }}
                justifyContent="space-between"
                alignItems="center" >
                     {avatar != false  && (
                        <>
                        {avatar == null? (
                        //    TODO: break out Avatar to common next component
                            // `flexShrink: 0` for footprint parity (§19): MUI's `Avatar` and
                            // `CompanyAvatar` both set it themselves, but `Skeleton` does not,
                            // so in a narrow header the placeholder would compress where the
                            // avatar it stands in for would not — a layout shift on load, in
                            // the one state that exists to prevent one.
                            <Skeleton variant="circular" sx={{ flexShrink: 0 }}>
                                <Avatar sx={{ width: 64, height: 64 }}/>
                            </Skeleton>
                        ) : avatar.isCompany ? (
                            <CompanyAvatar logo={avatar.url} name={avatar.name ?? avatar.letters} size={64} />
                        ) : (
                            <Avatar
                                src={avatar.url}
                                sx={{
                                    width: 64,
                                    height: 64,
                                    // Match the zinc initials treatment used by the sidebar +
                                    // table user blocks (initialsAvatarSx in Cells.tsx) so the
                                    // placeholder is consistent. Company avatars keep grey + icon.
                                    ...(!avatar.url && avatar.isCompany
                                        ? { bgcolor: theme.palette.grey[50], border: `1px solid ${theme.palette.grey[200]}` }
                                        : !avatar.url
                                            ? {
                                                bgcolor: theme.palette.primary[100],
                                                color: theme.palette.text.secondary,
                                                border: `1px solid ${theme.palette.primary[50]}`,
                                                fontWeight: 600,
                                            }
                                            : {}),
                                }}
                            >
                                {!avatar.url && avatar.isCompany ? (
                                    <Building2 strokeWidth={1} size={28} color={theme.palette.grey[400]} />
                                ) : (
                                    avatar.letters
                                )}
                            </Avatar>
                        )}
                        </>
                    )} 
                    {title && (
                        // Column so a subtitle can sit under the title. With no subtitle this
                        // renders exactly as before — one Typography in a zero-gap Stack — so the
                        // measured 68.5px title centre every other page depends on is unchanged.
                        <Stack spacing={subtitle ? 1 : 0} sx={{ minWidth: 0 }}>
                            {/* Row so the tour invitation can sit BESIDE the title without
                                becoming part of its line box. The chip renders null on every
                                page that has no untaken tour, which is almost all of them —
                                so for those pages this is an empty Stack around the same
                                Typography and the 68.5px title centre is untouched. */}
                            <Stack direction="row" alignItems="center" spacing={3} sx={{ minWidth: 0 }}>
                                {/* noWrap: page titles now carry the RECORD's name ("Edit The
                                    Complete Snowboard", VRG-103), so an unbounded title is no
                                    longer a fixed short string the designer controls — it is
                                    user data of any length. Without this it wraps to a second
                                    line and pushes the title row off the sidebar-logo datum
                                    that §29 keeps it aligned to. The Stack above already
                                    carries minWidth:0, which is what lets this shrink inside
                                    the flex row rather than overflowing it.

                                    The native `title` attribute, not a MUI Tooltip: this is a
                                    plain string and the browser's own tooltip is the cheaper
                                    way to recover a name the ellipsis ate. */}
                                <Typography
                                    variant="h5"
                                    color="text.primary"
                                    noWrap
                                    title={typeof title === 'string' ? title : undefined}
                                    sx={{
                                        // The one element in the header that yields under
                                        // pressure — see the note on the row Stack above.
                                        // `noWrap` already gives `overflow: hidden`, so the
                                        // automatic minimum is 0; this states it, matching the
                                        // certificate cell's title (`Cells.tsx`), which solves
                                        // the same problem one row down.
                                        minWidth: 0,
                                        fontFamily: 'Lato, Montserrat, sans-serif',
                                        fontSize: '24px',
                                        fontWeight: 600,
                                        fontStyle: 'normal',
                                        lineHeight: '130%',
                                        letterSpacing: 0,
                                    }}
                                >
                                    {title}
                                </Typography>
                                {/* PAGE headers only. `Header` renders twice on every
                                    /settings/* route — the shell's own "Settings" header
                                    plus the section's `nested` one — and both called
                                    `TakeATourChip`, which reads the DOM for this page's
                                    anchors and therefore returned the SAME tour to both.
                                    Reported as two identical "Take a tour" chips on
                                    /settings/features, one beside "Settings" and one
                                    beside "Features"; measured, both at 122.73x32 with
                                    the same label, so it was one invitation drawn twice
                                    rather than two tours.

                                    `nested` is the right discriminator because it already
                                    means exactly this — "a section title inside a panel,
                                    not the page title" — so no new concept is needed, and
                                    the chip's own contract is "beside the page title".
                                    Keeping the OUTER one also puts the chip in the same
                                    place on every route in the app, rather than "beside
                                    whichever heading happens to be innermost".

                                    Known cost: on /settings/* the surviving chip sits in
                                    the sticky shell header, so it stays on screen while
                                    you scroll, and it names the section's tour beside the
                                    word "Settings". Both are acceptable — the chip is
                                    conditional and disappears for good once engaged — but
                                    if that reads wrong, the alternative is to keep the
                                    innermost header's chip instead, which is the same
                                    one-line gate inverted. */}
                                {!nested && <TakeATourChip />}
                                {titleAdornment}
                            </Stack>
                            {subtitle && (
                                <Typography variant="body3" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    {status != false && (
                         // A Box, not a Fragment, so the chip is a flex item that can be told
                         // `flexShrink: 0` — it is a status indicator and never truncates (see
                         // the row Stack's note). Still exactly ONE child of the Stack, so the
                         // 12px gap either side is unchanged.
                         <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                         {status == null? (
                            // A PILL, not a text bar. `status={null}` is the loading placeholder for
                            // a StatusChip, and the chip is a 32px-tall rounded pill — measured 76x32
                            // on /owners/:id. The old `<Skeleton width={120}/>` was a text-variant
                            // bar of the default line height, i.e. neither the chip's shape nor its
                            // height. Width is a literal because a chip's width is its label's, which
                            // is exactly what a loading state does not know; it affects nothing
                            // vertically. Nothing passed `status={null}` before this sweep, so this
                            // changes no existing page.
                            <Skeleton variant="rounded" width={76} height={32} sx={{ borderRadius: '999px' }}/>
                         ) : (
                            <StatusChip statuses={status?.statuses} status={status?.value} />
                         )}
                         </Box>
                    )}
                    {!stackControls && leftComponents && (
                        // `flexShrink: 0` — this slot carries the page's status INDICATORS (the
                        // certificate page puts the Opened/seal pill, the invalid-scan count and
                        // the group-role glyph here). They are the signals a reader is looking
                        // for; the title is what gives way instead. See the row Stack's note.
                        <Stack sx={{ ml: 2, flexShrink: 0 }}>
                            {leftComponents}
                        </Stack>
                    )}
                    {/* {total != false && (
                        <Typography component="span" variant="h5" color="text.secondary">
                            {total == null ? <Skeleton width={32}/> : total}
                        </Typography>
                    )} */}

                </Stack>
                {/* The right-hand side of the title row: the PAGE's own action group (if
                    any), plus — as a visually separated, always-present slot — the
                    global Ask Vera launcher. Wrapped as ONE flex child so the outer
                    Stack's `justifyContent="space-between"` still sees exactly two
                    columns; a bare third sibling here would be spread to a third
                    position instead of sitting beside the page's own actions.

                    The launcher is intentionally NOT part of `rightComponents` — it is
                    global chrome that happens to render on this page, not one of this
                    page's actions, so it must never displace a page's primary action
                    from the rightmost slot (§26/§33) or read as a member of that
                    action group. A `Divider` is what draws that line when both are
                    present; on a page with no `rightComponents` the launcher renders
                    alone and no divider is needed.

                    Gated `!nested` for the launcher only (not for `rightComponents`,
                    which nested settings sections still use for their own Save/etc.
                    actions) — `Header` renders twice on every /settings/* route (the
                    shell's outer header + the section's `nested` one), and a second
                    launcher there would be the same double-render `TakeATourChip`
                    already guards against a few lines up. */}
                {((!stackControls && rightComponents) || (!nested && !hideAskLauncher)) && (
                    // `flexShrink: 0` — the action group is the other thing that must not be
                    // squeezed by a long title. Before this, the title held its full width and
                    // pushed this column clean off the right edge of the page (measured at
                    // 1024w on a certificate detail: this ran to x=1177 inside a 999px row, so
                    // "Add tag", the kebab and the Ask launcher were simply not reachable).
                    <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
                        {!stackControls && rightComponents && (
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                                sx={headerActionGroupSx(actionSize)}
                            >
                            {rightComponents}
                            </Stack>
                        )}
                        {!nested && !hideAskLauncher && (
                            <>
                                {!stackControls && rightComponents && (
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ height: 24, alignSelf: 'center', borderColor: theme.palette.primary[100] }}
                                    />
                                )}
                                <AskVeraLauncher />
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
            {/* Page description (§40). A sibling ROW of the title row, not a child of the title's
                column, so it spans the header's full width instead of being squeezed against the
                action group. `lineHeight: 1.5` because descriptive copy at the default leading
                reads dense (§15). */}
            {description && (
                <Box className="vera-page-description">
                    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {description}
                    </Typography>
                </Box>
            )}
            {stackControls && (leftComponents || rightComponents) && (
                <Stack
                    direction="row"
                    spacing={3}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%' }}
                >
                    <Stack>{leftComponents}</Stack>
                    {rightComponents && <Stack sx={headerActionGroupSx(actionSize)}>{rightComponents}</Stack>}
                </Stack>
            )}
        </Stack>
    )
})

Header.displayName = 'Header'
